import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import teamicon from "../../../assets/teamicon.png";
import { jwtDecode } from "jwt-decode";
import ConfirmationModal from "../../../utils/general/confirmationModal";
import { showToast } from "../../../components/ToastNotification";
import { LogOut } from "lucide-react";

const PlayerTeamsTab = ({ player }) => {
  const token = useSelector((state) => state.user.token);
  const [teams, setTeams] = useState([]);
  const [error, setError] = useState("");
  const [userId, setUserId] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState(null);

  useEffect(() => {
    if (token) {
      const decodedToken = jwtDecode(token);
      setUserId(decodedToken.id);
    }
  }, [token]);

  const fetchTeams = async () => {
    try {
      const response = await fetch(
        `https://server-postgre.vercel.app/api/players/${player.id}/teams`,
        {
          method: "GET",
        }
      );

      if (!response.ok) throw new Error("Error al obtener los equipos");

      const data = await response.json();
      const formattedData = Array.isArray(data)
        ? data.map((team) => ({
            id: team.team_id,
            imageSrc: team.picture_url || teamicon,
            name: team.team_name,
            sub: team.team_category ? `Sub ${team.team_category}` : "",
            group: team.group_name || "",
          }))
        : [];

      setTeams(formattedData);
    } catch (error) {
      setError(error.message);
    }
  };

  useEffect(() => {
    if (userId) fetchTeams();
  }, [userId]);

  const handleLeaveClick = (teamId) => {
    setSelectedTeamId(teamId);
    setIsModalOpen(true);
  };

  const handleLeaveConfirm = async () => {
    try {
      const url = `https://server-postgre.vercel.app/api/players/${player.id}/teams/${selectedTeamId}`;

      const response = await fetch(url, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Error al abandonar el equipo.");

      await fetchTeams();
      showToast.success("Has abandonado el equipo correctamente.");
    } catch (error) {
      showToast.error(
        error.message || "Hubo un error al intentar abandonar el equipo."
      );
    } finally {
      setIsModalOpen(false);
      setSelectedTeamId(null);
    }
  };

  const handleLeaveCancel = () => {
    setIsModalOpen(false);
    setSelectedTeamId(null);
  };

  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div className="bg-gray-50 p-4 rounded-3xl">
      <h2 className="text-lg font-bold text-gray-800 mb-4">Mis Equipos</h2>

      {teams.length === 0 ? (
        <p className="text-gray-500 text-center py-6">
          No perteneces a ningún equipo aún.
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {teams.map((team) => (
            <div
              key={team.id}
              className="relative group flex flex-col items-center gap-1 p-2 bg-white rounded-xl shadow-sm hover:shadow-md transition"
            >
              {/* Botón hover para abandonar equipo */}
              {userId === player.user_id && (
                <button
                  onClick={() => handleLeaveClick(team.id)}
                  className="absolute top-2 right-2 text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition"
                  title="Abandonar equipo"
                >
                  <LogOut size={18} />
                </button>
              )}

              <img
                src={team.imageSrc}
                alt={team.name}
                className="w-16 h-16 rounded-full object-cover"
              />
              <span className="text-gray-800 font-semibold text-center text-sm">
                {team.name}
              </span>
              {team.sub && (
                <span className="text-gray-500 text-xs text-center">
                  {team.sub}
                </span>
              )}
              {team.group && (
                <span className="text-gray-500 text-xs text-center">
                  {team.group}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleLeaveCancel}
        onConfirm={handleLeaveConfirm}
        message="¿Estás seguro de que deseas abandonar este equipo?"
      />
    </div>
  );
};

export default PlayerTeamsTab;
